import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  DialogService,
  DIALOG_RETURN_TYPES,
  FormDialogComponent,
} from '@next/core-lib/dialog';

import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import {
  getCustomerProducts,
  getProducts,
} from 'src/app/product/store/selectors/product.selectors';

import { Product, ViewMode } from 'src/app/shared/models/shared.model';

import { DosatorConversions, DosatorProduct, DosatorSetting } from '../../models/settings.model';

import {
  setSelectedDosatorSetting,
  upsertDosatorSettingRequest,
} from '../../store/actions/settings.actions';
import { getSelectedDosatorSetting } from '../../store/selectors/settings.selectors';

import { DosatorSettingProductComponent } from '../dosator-setting-product-item/dosator-setting-product-item';

export interface DosatorSettingItemDialogInputData extends AbstractFormDialogData {
  dosatorSettingId?: number;
  viewMode: ViewMode;
}
export interface DosatorSettingItemDialogResultData {
  dosatorSetting: DosatorSetting;
}

@Component({
  selector: 'app-dosator-setting-item-dialog',
  templateUrl: './dosator-setting-item-dialog.component.html',
  styleUrls: ['./dosator-setting-item-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    MatTabGroup,
    NgClass,
    MatTab,
    MatTabLabel,
    DosatorSettingProductComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DosatorSettingItemDialogComponent extends AbstractDialog<DosatorSettingItemDialogResultData> {
  formGroup: FormGroup;
  productFormGroups: FormGroup[] = [];
  conversionFormGroups: FormGroup[] = [];

  dosatorProductTabsClass: string;

  isMobile$ = this.breakpointService.ltSm$;

  id: number;

  dosatorSetting: DosatorSetting;
  tabs: number[] = [];

  selectedTabIndex = 0;
  productDeleted = false;
  productAdded = false;
  canDeleteProduct = false;

  products$: Observable<Product[]> = this.store.select(getProducts).pipe(
    withLatestFrom(this.store.select(getCustomerProducts)),
    skipWhile(
      ([existingProducts, customerProducts]) =>
        existingProducts === undefined || customerProducts === undefined,
    ),
    map(([existingProducts, customerProducts]: [Product[], Product[]]) =>
      [...(existingProducts ?? []), ...(customerProducts ?? [])].map(
        (product) =>
          ({
            id: product.id,
            code: product.code,
            name: product.name,
            suitablePhases: product.suitablePhases,
            ewKg: product.ewKg,
            lysKg: product.lysKg,
            customerId: product.customerId,
            country: product.country,
            energySystem: product.energySystem,
            lysineSystem: product.lysineSystem,
            unitNettoPrice: product.unitNettoPrice,
            currency: product.currency,
            schemeTypes: product.schemeTypes,
          }) as Product,
      ),
    ),
    take(1),
  );
  products: Product[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DosatorSettingItemDialogInputData,
    protected mdDialogRef: MatDialogRef<
      DosatorSettingItemDialogComponent,
      AbstractDialogResultData<DosatorSettingItemDialogResultData>
    >,
    @Inject(BreakpointServiceInjector)
    private readonly breakpointService: BreakpointService,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly formBuilder: FormBuilder,
    private readonly dialog: DialogService,
  ) {
    super();

    this.isMobile$.subscribe((isMobile) => {
      this.dosatorProductTabsClass = isMobile ? 'mobile' : '';
    });

    this.id = this.data.dosatorSettingId;

    if (this.id && this.id > 0) {
      this.data.title = this.localeService.translate(
        'settings.dosator-setting.edit-dosatorsetting',
      );

      this.store.dispatch(setSelectedDosatorSetting({ dosatorSettingId: this.id }));
      this.store
        .select(getSelectedDosatorSetting)
        .pipe(
          skipWhile((c) => c == null),
          take(1),
        )
        .subscribe((dosatorSetting) => {
          this.dosatorSetting = dosatorSetting;
          this.buildFormGroups();
          this.setFormValues();
        });
    } else {
      this.data.title = this.localeService.translate('settings.dosator-setting.add-dosatorsetting');
      this.tabs = [0];

      const conversions = this.getDefaultConversions();

      const products: DosatorProduct[] = [];

      products.push({
        productId: undefined,
        conversions,
      });

      this.dosatorSetting = {
        name: undefined,
        products,
      };
      this.buildFormGroups();
    }
  }

  getDefaultConversions(): DosatorConversions[] {
    const conversions: DosatorConversions[] = [];

    conversions.push({ liter: 0.5, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 1, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 1.5, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 2, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 2.5, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 3, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 3.5, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 4, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 4.5, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 5, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 5.5, kilogram: undefined, kilogramPerLiter: undefined });
    conversions.push({ liter: 6, kilogram: undefined, kilogramPerLiter: undefined });
    return conversions;
  }

  private buildFormGroups(): void {
    this.productFormGroups = [];

    this.dosatorSetting.products.forEach((product) => {
      this.buildProductsFormGroup(product);
    });

    this.formGroup = this.formBuilder.group({
      name: new FormControl('', [Validators.required]),
      products: this.productFormGroups,
    });
  }

  private buildProductsFormGroup(product: DosatorProduct) {
    const formArray = this.formBuilder.array(
      product.conversions.map((line) => this.createConversionFormControl(line)),
    );
    const conversionFormGroup = this.formBuilder.group({ lines: formArray });

    this.conversionFormGroups.push(conversionFormGroup);

    this.productFormGroups.push(
      this.formBuilder.group({
        productId: [null, [Validators.required]],
        conversions: conversionFormGroup,
      }),
    );
  }

  private createConversionFormControl(line: DosatorConversions): FormGroup {
    const kilogram = new FormControl(line.kilogram, [Validators.required]);
    const liter = new FormControl(line.liter, [Validators.required]);
    const kilogramPerLiter = new FormControl(line.kilogramPerLiter, [Validators.required]);

    return new FormGroup({
      liter,
      kilogram,
      kilogramPerLiter,
    });
  }

  private setFormValues() {
    this.tabs = [];

    this.formGroup.controls.name.setValue(this.dosatorSetting.name);

    this.dosatorSetting.products.forEach((product, index) => {
      this.tabs.push(index);

      if (product.productId > 0) {
        this.productFormGroups[index].controls.productId.setValue(product.productId);
      }
    });

    this.canDeleteProduct = this.tabs.length > 1;
  }

  save() {
    if (this.validateForm()) {
      const dosatorsetting = {
        name: <string>this.formGroup.controls.name.value,
        products: this.getProductsFromFormGroups(),
      } as DosatorSetting;

      if (this.id && this.id > 0) {
        dosatorsetting.id = this.id;
      }

      this.confirm({
        dosatorSetting: dosatorsetting,
      });
    }
  }

  private getProductsFromFormGroups(): DosatorProduct[] {
    const dosatorProducts: DosatorProduct[] = [];

    this.productFormGroups.forEach((dosatorProductGroup) => {
      const conversions: DosatorConversions[] = [];
      const converionFormGroup = dosatorProductGroup.controls.conversions as FormGroup;

      const conversionLines = converionFormGroup.controls.lines as FormArray;

      conversionLines.controls.forEach((line: FormGroup) => {
        conversions.push({
          liter: line.controls.liter.value,
          kilogram: line.controls.kilogram.value,
          kilogramPerLiter: line.controls.kilogram.value / line.controls.liter.value,
        });
      });

      dosatorProducts.push({
        productId: dosatorProductGroup.controls.productId.value,
        conversions,
      });
    });

    return dosatorProducts;
  }

  //save and create new dosatorsetting product (NOT new dosator setting !)
  alt() {
    if (this.validateForm()) {
      this.dosatorSetting = {
        ...this.dosatorSetting,
        name: <string>this.formGroup.controls.name.value,
        products: this.getProductsFromFormGroups(),
      };
      this.store.dispatch(
        upsertDosatorSettingRequest({
          dosatorSetting: this.dosatorSetting,
        }),
      );
      this.store
        .select(getSelectedDosatorSetting)
        .pipe(
          skipWhile((c) => !c),
          take(1),
        )
        .subscribe((dosatorSetting) => {
          this.dosatorSetting = dosatorSetting;
        });
      const newDosatorProducts: DosatorProduct[] = [];
      this.dosatorSetting.products.forEach((product) => {
        newDosatorProducts.push(product);
      });
      newDosatorProducts.push({
        productId: undefined,
        conversions: this.getDefaultConversions(),
      });
      this.dosatorSetting = {
        ...this.dosatorSetting,
        products: newDosatorProducts,
      };
      this.buildFormGroups();
      this.setFormValues();
      this.selectedTabIndex = newDosatorProducts.length;
      this.productAdded = true;
    }
  }

  private validateForm(): boolean {
    if (
      !(
        this.formGroup.valid &&
        this.productFormGroups.every((f) => f.valid) &&
        this.conversionFormGroups.every((f) => f.valid)
      )
    ) {
      this.focusOnInvalidTab();
      return false;
    }
    return true;
  }

  private focusOnInvalidTab() {
    const indexOfFailedTab = this.productFormGroups.findIndex((f: FormGroup) => !f.valid);
    if (indexOfFailedTab >= 0) {
      this.validateAllFormFields(this.productFormGroups[indexOfFailedTab]);
      const conversions = this.productFormGroups[indexOfFailedTab].controls
        .conversions as FormGroup;

      const lines = conversions.controls.lines as FormArray;

      if (lines) {
        lines.controls.forEach((line: FormGroup) => {
          this.validateAllFormFields(line);
        });
      }

      this.selectedTabIndex = indexOfFailedTab;
    }
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  delete(productIndex: number) {
    this.dialog
      .confirmWarning({
        title: this.localeService.translate(
          'settings.dosator-setting.delete-dosator-product-confirmation.title',
        ),
        text: this.localeService.translate(
          'settings.dosator-setting.delete-dosator-product-confirmation.message',
        ),
        pushHistoryState: false,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          const newProducts: DosatorProduct[] = [];
          this.dosatorSetting.products.forEach((product) => {
            newProducts.push(product);
          });
          newProducts.splice(productIndex, 1);
          this.dosatorSetting = {
            ...this.dosatorSetting,
            products: newProducts,
          };
          this.buildFormGroups();
          this.setFormValues();
          this.selectedTabIndex =
            this.dosatorSetting.products.length >= productIndex ? productIndex : productIndex - 1;
          this.productDeleted = true;
        }
      });
  }
}
